import React, { useState } from "react";
import { useDispatch } from "react-redux";
import InputMask from "react-input-mask";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import LoginIcon from "@mui/icons-material/Login";
import {
  Button,
  FormControl,
  FormHelperText,
  FormLabel,
  Input,
  Modal,
  ModalDialog,
  ModalOverflow,
  Stack,
  Typography,
} from "@financeable-com-au/financeable-ui";
import { ERRORS_MESSAGE } from "../../constants";
import regex from "../../utils/regex";
import { formatToISODate, parseReadableDate } from "../../utils/formatDate";
import { saveValue } from "../../store/slices/customerSlice";
import { customerLogin } from "../../store/slices/applicationFormSlice";
import { openSnackbar } from "../../store/slices/snackbarSlice";

const initialFormValues = {
  email: "",
  dateOfBirth: "",
};

const readableDateLength = "DD-MM-YYYY".length;

const getEmailError = (value) => {
  const email = value.trim();
  return email && !regex.email.test(email.toLowerCase()) ? "Please enter a valid email address." : "";
};

const getDateOfBirthError = (value, required = false) => {
  const dateOfBirth = value.trim();

  if (!dateOfBirth) return required ? "Please enter your date of birth." : "";

  return dateOfBirth.length !== readableDateLength || !parseReadableDate(dateOfBirth)
    ? "Please enter a valid date of birth."
    : "";
};

const CustomerLoginModel = ({
  customerId,
  applicationId,
  workOSOrgId,
  openCustomerLoginModel,
  handleCloseCustomerLoginModel,
}) => {
  const dispatch = useDispatch();

  const [values, setValues] = useState(initialFormValues);
  const [fieldErrors, setFieldErrors] = useState(initialFormValues);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const email = values.email.trim();
  const dateOfBirth = values.dateOfBirth.trim();
  const isSubmitDisabled = !email || !dateOfBirth || !!fieldErrors.email || !!fieldErrors.dateOfBirth || isSubmitting;

  const handle = {
    login: async (event) => {
      event?.preventDefault();

      const formErrors = {
        email: getEmailError(values.email),
        dateOfBirth: getDateOfBirthError(values.dateOfBirth, true),
      };
      setFieldErrors(formErrors);

      if (!email || !dateOfBirth || Object.values(formErrors).some(Boolean) || isSubmitting) return;

      setIsSubmitting(true);

      const data = {
        customerId,
        applicationId,
        workOSOrgId,
        email,
        dateOfBirth: formatToISODate(dateOfBirth),
      };
      const res = await dispatch(customerLogin(data));
      setIsSubmitting(false);

      if (res.payload?.data?.data?.token) {
        sessionStorage.setItem("token", res.payload?.data?.data?.token);
        handleCloseCustomerLoginModel();
        dispatch(saveValue({ isCustomerLogin: true }));
      } else {
        sessionStorage.removeItem("token");
        if (res.payload?.response_error) {
          dispatch(
            openSnackbar({
              message: res.payload?.status_message || ERRORS_MESSAGE.fetchErrorMsg,
              intent: "danger",
            }),
          );
        }
      }
    },
    onChangeEmail: (value) => {
      setValues((previousValues) => ({
        ...previousValues,
        email: value,
      }));

      setFieldErrors((previousErrors) => ({
        ...previousErrors,
        email: getEmailError(value),
      }));
    },
    onChangeDateOfBirth: (value) => {
      setValues((previousValues) => ({
        ...previousValues,
        dateOfBirth: value,
      }));

      setFieldErrors((previousErrors) => ({
        ...previousErrors,
        dateOfBirth: getDateOfBirthError(value),
      }));
    },
    onBlurDateOfBirth: (value) => {
      setFieldErrors((previousErrors) => ({
        ...previousErrors,
        dateOfBirth: getDateOfBirthError(value),
      }));
    },
    closeModal: (...args) => {
      if (isSubmitting) return;
      handleCloseCustomerLoginModel(...args);
    },
  };

  return (
    <Modal
      aria-labelledby="customer-sign-in-title"
      aria-describedby="customer-sign-in-description"
      open={!!openCustomerLoginModel}
      onClose={handle.closeModal}
      disableEscapeKeyDown
    >
      <ModalOverflow>
        <ModalDialog minWidth="min(420px, calc(100vw - 32px))" maxWidth="min(480px, calc(100vw - 32px))">
          <form onSubmit={handle.login}>
            <Stack rowGap={5}>
              <Stack rowGap={1}>
                <Typography component="h2" id="customer-sign-in-title" level="title-lg" bold>
                  Customer sign in
                </Typography>
                <Typography id="customer-sign-in-description" level="body-sm" textColor="text.secondary">
                  Enter your email address and date of birth to access your application details.
                </Typography>
              </Stack>

              <FormControl error={!!fieldErrors.email}>
                <FormLabel size="md">Email</FormLabel>
                <Input
                  autoFocus
                  placeholder="Your email address"
                  type="email"
                  name="email"
                  value={values.email}
                  onChange={(event) => handle.onChangeEmail(event.target.value)}
                  size="lg"
                />
                {!!fieldErrors.email && (
                  <FormHelperText>
                    <ErrorOutlineIcon sx={{ fontSize: "1rem" }} />
                    {fieldErrors.email}
                  </FormHelperText>
                )}
              </FormControl>

              <FormControl error={!!fieldErrors.dateOfBirth}>
                <FormLabel size="md">Date of birth</FormLabel>
                <InputMask
                  mask="99-99-9999"
                  value={values.dateOfBirth}
                  disabled={isSubmitting}
                  maskChar={null}
                  onChange={(event) => handle.onChangeDateOfBirth(event.target.value)}
                  onBlur={(event) => handle.onBlurDateOfBirth(event.target.value)}
                >
                  {() => (
                    <Input placeholder="DD-MM-YYYY" type="text" name="dateOfBirth" disabled={isSubmitting} size="lg" />
                  )}
                </InputMask>
                {!!fieldErrors.dateOfBirth && (
                  <FormHelperText>
                    <ErrorOutlineIcon sx={{ fontSize: "1rem" }} />
                    {fieldErrors.dateOfBirth}
                  </FormHelperText>
                )}
              </FormControl>

              <Button
                disabled={isSubmitDisabled}
                loading={isSubmitting}
                type="submit"
                variant="primary"
                size="lg"
                endDecorator={<LoginIcon />}
              >
                Login
              </Button>
            </Stack>
          </form>
        </ModalDialog>
      </ModalOverflow>
    </Modal>
  );
};

export default CustomerLoginModel;
